'use client';

import { useRouter, useSearchParams } from 'next/navigation';
import { useCallback, useEffect, useState } from 'react';

import { WebViewScreen } from '@diritto/common/app/webview-header.constant';
import { CategoryType, ContentOrderType } from '@diritto/common/content/constants';
import { ContestId } from '@diritto/common/content/contest.constants';
import { SectionPosition } from '@diritto/common/home/constants/section-constants';
import { TrackerExtraInfo, TrackerPosition, TrackingEventType } from '@diritto/common/tracker/tracking-event.constant';

import { ContentSearchOrderTab, ContentSearchParams } from '@/app-components/search/ContentSearchOrderTab';
import { HomeSectionViewMode } from '@/constants/homeSectionViewMode';
import { useGetSectionsQuery } from '@/queries/relay/useGetSectionsQuery';
import Section from 'app-components/home/sections/Section';
import { ContentSearchResultsWrapper } from 'app-components/search/ContentSearchResultsWrapper';
import { SearchInput } from 'app-components/search/SearchInput';
import { extractContentContestIds, useActiveContentContestIdsDispatch } from 'contexts/ActiveContentContestIdsProvider';
import { useAuthState } from 'contexts/AuthProvider';
import { useTrackingState } from 'contexts/TrackingProvider';
import { ContentBodyContainer } from 'ui-components/layout/containers/ContentBodyCenterContainer';
import { MutableHeaderLayout } from 'ui-components/layout/MutableHeaderLayout';
import { useInAppHeader } from 'ui-hooks/useInAppHeader';
import { useInAppStatusBar } from 'ui-hooks/useInAppStatusBar';

const trackerExtraInfo: TrackerExtraInfo = {
  trackerPosition: TrackerPosition.Search,
};

export const SearchPage = ({ isApp }: { isApp?: boolean }) => {
  useInAppHeader({ screen: WebViewScreen.Search, title: '검색' });
  useInAppStatusBar(WebViewScreen.Search);

  const router = useRouter();
  const searchParams = useSearchParams();

  const query = searchParams?.get('q') ?? '';
  const categoryType = searchParams?.get('categoryType') as CategoryType;
  const contentOrderType = searchParams?.get('contentOrderType') as ContentOrderType;
  const onlySerialized = searchParams?.get('showOnlySerialized') === 'true';

  const [totalCount, setTotalCount] = useState(0);
  const [keyword, setKeyword] = useState(query);
  const { tracker } = useTrackingState();
  const { user } = useAuthState();
  const { checkActiveContentContests } = useActiveContentContestIdsDispatch();

  const { data: searchSectionData } = useGetSectionsQuery({
    includeAdult: !!user?.isAdult,
    position: SectionPosition.Search,
  });

  useEffect(() => {
    if (searchSectionData?.contents) {
      const contentIds = Object.keys(searchSectionData.contents).map(Number);
      const contentContestIds = extractContentContestIds(contentIds, searchSectionData.contents);
      checkActiveContentContests(ContestId.Contest2026, contentContestIds);
    }
  }, [searchSectionData?.contents, checkActiveContentContests]);

  const handleSearch = useCallback(
    (q: string, contentSearchParams?: ContentSearchParams) => {
      const defaultParams = {
        categoryType,
        contentOrderType,
        onlySerialized,
      };

      const mergedParams = {
        ...defaultParams,
        ...contentSearchParams,
      };

      const params = new URLSearchParams({
        q: q.trim(),
        ...(mergedParams.categoryType && { categoryType: mergedParams.categoryType }),
        ...(mergedParams.contentOrderType && { contentOrderType: mergedParams.contentOrderType }),
        ...(mergedParams.onlySerialized && {
          showOnlySerialized: mergedParams.onlySerialized.toString(),
        }),
      });

      return router.push(`/search?${params.toString()}`);
    },
    [router, categoryType, contentOrderType, onlySerialized],
  );

  const handleFilterChange = useCallback(
    (contentSearchParams: ContentSearchParams) => {
      handleSearch(query, contentSearchParams);
    },
    [handleSearch, query],
  );

  useEffect(() => {
    tracker.track(TrackingEventType.ViewSearch);
  }, [tracker]);

  useEffect(() => {
    setKeyword(query);

    if (query) {
      tracker.track(TrackingEventType.Search, { searchKeyword: query });
    }
  }, [query, tracker]);

  return (
    <MutableHeaderLayout title="검색" isApp={isApp}>
      <ContentBodyContainer>
        <section className="flex items-center justify-center pt-6">
          <SearchInput onSearch={handleSearch} keyword={keyword} setKeyword={setKeyword} />
        </section>
        {!!query && (
          <>
            <ContentSearchOrderTab
              totalCount={totalCount}
              categoryType={categoryType ?? CategoryType.All}
              contentOrderType={contentOrderType ?? ContentOrderType.Best}
              onlySerialized={onlySerialized ?? false}
              onChange={handleFilterChange}
            />
            <div className="flex flex-col">
              <ContentSearchResultsWrapper
                keyword={query}
                setTotalCount={setTotalCount}
                categoryType={categoryType ?? CategoryType.All}
                contentOrderType={contentOrderType ?? ContentOrderType.Best}
                onlySerialized={onlySerialized ?? false}
              />
            </div>
          </>
        )}
      </ContentBodyContainer>
      {!query && (
        <div className="pt-6 flex flex-col gap-8">
          {searchSectionData?.sections.map(section => (
            <Section
              overrideViewMode={HomeSectionViewMode.Default}
              key={section.id}
              section={section}
              contents={searchSectionData.contents}
              episodes={searchSectionData.episodes}
              profileLites={searchSectionData.profileLites || {}}
              replies={searchSectionData.replies}
              fancyShares={searchSectionData.fancyShares}
              rollings={searchSectionData.rollings}
              trackerExtraInfo={trackerExtraInfo}
            />
          ))}
        </div>
      )}
    </MutableHeaderLayout>
  );
};
